/**
 * Modal "Captura de leads" (spec §4.8; mockups lote-a 01–03/05).
 *
 * Estados (derivados da sessão no cache React Query — polling 5s):
 * prepare → qr → filled → confirmed → done. `cancelled` fecha.
 *
 * Os passos `filled`/`confirmed`/`done` (mockups 02/03/05) são implementados
 * pela Task 14 em `CaptureFilledStep`/`CaptureConfirmedStep`/`CaptureDoneStep`.
 *
 * Props consumidas pela Parte 5 (dashboard do vendedor): NÃO renomear.
 */
import { DEFAULT_GAMIFICATION_RULES, type GamificationPeriod, type LeadCaptureSession } from '@troia-v3/types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button, Modal, ModalContent, ModalFooter, ModalHeader } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { useCancelCaptureSession, useCompleteCapture, useCreateCaptureSession, useLeadCaptureOptions, useLeadCaptureSession, useSkipCaptureForm, useSubmitCaptureForm } from '../hooks/useLeadCapture';

import { CaptureConfirmedStep } from './CaptureConfirmedStep';
import { CaptureDoneStep } from './CaptureDoneStep';
import { CaptureEmptyState } from './CaptureEmptyState';
import { CaptureFilledStep } from './CaptureFilledStep';
import { CAPTURE_FORM_STEP_TITLE, CaptureFormStep } from './CaptureFormStep';
import { CapturePrepareStep, type CapturePrepareValue } from './CapturePrepareStep';
import { CaptureQrStep } from './CaptureQrStep';
import { CaptureModalHeader } from './captureUi';

export interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Retomar uma sessão pendente (dashboard do vendedor — Parte 5). */
  resumeSessionUuid?: string;
  /** Chamado UMA vez quando a sessão termina em `done`, com os pontos ganhos. */
  onCompleted?: (pointsEarned: number) => void;
  /**
   * Período da gamificação da empresa — rotula "+N pontos {no mês|na semana|
   * no trimestre}" na tela final. Quem tem o dado é o dashboard do vendedor
   * (`SellerDashboardResponse.gamificationPeriod`); nos outros pontos de
   * entrada da modal (CRM) fica ausente e o rótulo cai no padrão mensal, que
   * é o default da config de gamificação.
   */
  gamificationPeriod?: GamificationPeriod;
  /** Abre a configuração do funil (mockup 01-C). Ausente = botão não aparece. */
  onConfigureFunnel?: () => void;
}

/**
 * `'form'` (Parte 3, Task 12) é um sub-estado de UI, não de sessão: entra
 * quando o vendedor clica "Continuar atendimento" com `formOpen=true` e a
 * sessão ainda está em `filled`/`confirmed`. A sessão `form_completed` mapeia
 * para `'confirmed'` quando o funil tem canal de confirmação e para
 * `'filled'` quando não tem (ver a derivação de `view` abaixo).
 */
export type CaptureView = 'loading' | 'empty' | 'prepare' | 'qr' | 'filled' | 'confirmed' | 'form' | 'done' | 'error';

const rulePoints = (key: 'lead_captured' | 'form_completed'): number => DEFAULT_GAMIFICATION_RULES.find((r) => r.key === key)?.points ?? 0;

/**
 * ESTIMATIVA pelos defaults do catálogo — só serve de reserva.
 *
 * Ela não sabe se a gamificação está ligada, se a regra foi desativada, se o
 * cap diário do dia estourou nem se a empresa mudou os pontos: prometia "+4"
 * para quem ganhou 0. O número verdadeiro é `session.pointsEarned`, gravado
 * pelo backend a partir do livro-caixa quando a sessão vira `done`
 * (G4 da onda final). Este cálculo só entra em sessão antiga, criada antes
 * do campo existir.
 */
export const computeSessionPoints = (session: LeadCaptureSession): number =>
  (session.confirmedAt && !session.wasExistingLead ? rulePoints('lead_captured') : 0)
  + (session.formCompletedAt ? rulePoints('form_completed') : 0);

/** Pontos REAIS da sessão, com a estimativa do catálogo só como retrocompatibilidade. */
export const sessionPoints = (session: LeadCaptureSession): number =>
  session.pointsEarned ?? computeSessionPoints(session);

export const LeadCaptureModal: React.FC<LeadCaptureModalProps> = ({ isOpen, onClose, resumeSessionUuid, onCompleted, onConfigureFunnel, gamificationPeriod = 'month' }) => {
  const [sessionUuid, setSessionUuid] = useState<string | null>(resumeSessionUuid ?? null);
  const [prepare, setPrepare] = useState<CapturePrepareValue>({ funnelId: '' });
  const [forcePrepare, setForcePrepare] = useState(false);
  /** Abre a etapa "Formulário" (Parte 3) por cima de `filled`/`confirmed` — ver `handleContinue`. */
  const [formOpen, setFormOpen] = useState(false);
  /** Mensagem da API para o erro de `createSession` (D15 auto-gerar sem tela de preparar). */
  const [createErrorMessage, setCreateErrorMessage] = useState<string | null>(null);
  const autoCreateTried = useRef(false);
  const completedNotified = useRef<string | null>(null);

  const options = useLeadCaptureOptions(isOpen);
  const sessionQuery = useLeadCaptureSession(isOpen ? sessionUuid : null);
  const createSession = useCreateCaptureSession();
  const cancelSession = useCancelCaptureSession();
  const skipForm = useSkipCaptureForm();
  const complete = useCompleteCapture();
  const submitForm = useSubmitCaptureForm();

  // Reset a cada abertura (inclui limpar um erro de geração de sessão anterior — sem isso,
  // reabrir a modal depois de uma falha no D15 mostraria o mesmo erro preso).
  useEffect(() => {
    if (!isOpen) return;
    setSessionUuid(resumeSessionUuid ?? null);
    setForcePrepare(false);
    setFormOpen(false);
    setCreateErrorMessage(null);
    createSession.reset();
    autoCreateTried.current = false;
    completedNotified.current = null;
  }, [isOpen, resumeSessionUuid]);

  // Defaults dos selects: única opção assumida; mais de uma → primeira pré-selecionada
  useEffect(() => {
    if (!options.data) return;
    setPrepare((prev) => ({
      funnelId: prev.funnelId || options.data.funnels[0]?.id || '',
      ...(options.data.teams.length > 0 ? { teamId: prev.teamId ?? options.data.teams[0]?.id } : {}),
      ...(options.data.units.length > 0 ? { unitId: prev.unitId ?? options.data.units[0]?.id } : {}),
    }));
  }, [options.data]);

  const needsPrepare = useMemo(() => {
    if (!options.data) return false;
    return options.data.funnels.length > 1 || options.data.teams.length > 1 || options.data.units.length > 1;
  }, [options.data]);

  const generate = useCallback((value: CapturePrepareValue) => {
    setCreateErrorMessage(null);
    createSession.mutate(
      { funnelId: value.funnelId, ...(value.teamId ? { teamId: value.teamId } : {}), ...(value.unitId ? { unitId: value.unitId } : {}) },
      {
        onSuccess: (res) => { setSessionUuid(res.session.uuid); setForcePrepare(false); },
        onError: (error: unknown) => {
          const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Não foi possível gerar o QR agora, tente de novo';
          setCreateErrorMessage(message);
          // Na tela "prepare" (multi-opção) o erro fica ali mesmo — só avisa por toast.
          // No fluxo automático D15 (sem tela de preparar) é a derivação de `view` que
          // assume o erro (abaixo), levando para `'error'` com retry.
          if (needsPrepare || forcePrepare) toast.error(message);
        },
      },
    );
  }, [createSession, needsPrepare, forcePrepare]);

  // D15: 1 funil, ≤1 equipe, ≤1 unidade → gera direto, sem tela de preparar
  useEffect(() => {
    if (!isOpen || sessionUuid || forcePrepare || !options.data || autoCreateTried.current) return;
    if (options.data.funnels.length === 1 && !needsPrepare) {
      autoCreateTried.current = true;
      const only = options.data.funnels[0];
      if (only) {
        generate({ funnelId: only.id, ...(options.data.teams[0] ? { teamId: options.data.teams[0].id } : {}), ...(options.data.units[0] ? { unitId: options.data.units[0].id } : {}) });
      }
    }
  }, [isOpen, sessionUuid, forcePrepare, options.data, needsPrepare, generate]);

  const session = sessionQuery.data?.session;

  // `done` → avisa quem abriu (uma vez por sessão)
  useEffect(() => {
    if (!session || session.status !== 'done' || completedNotified.current === session.uuid) return;
    completedNotified.current = session.uuid;
    onCompleted?.(sessionPoints(session));
  }, [session, onCompleted]);

  // `cancelled` vindo de outro lugar (outra aba) → fecha
  useEffect(() => {
    if (session?.status === 'cancelled') onClose();
  }, [session?.status, onClose]);

  const view: CaptureView = (() => {
    if (sessionUuid) {
      if (sessionQuery.isError) return 'error';
      if (!session) return 'loading';
      if (session.status === 'waiting_scan') return 'qr';
      // `wasExistingLead` (D5): o lead é de outro atendimento — o backend
      // responde 409 em `POST …/form`, então a etapa Formulário nunca abre.
      if (formOpen && (session.status === 'filled' || session.status === 'confirmed') && session.capture.formId && !session.wasExistingLead) return 'form';
      if (session.status === 'filled') return 'filled';
      if (session.status === 'confirmed') return 'confirmed';
      // `form_completed` só cai na tela "Confirmado no WhatsApp" quando existe
      // canal de confirmação no funil. Sem canal a sessão nunca passou pela
      // Etapa 2, e aquela tela apareceria com "… às " vazio (entre o submit do
      // formulário e o `complete`, e também na retomada da sessão) — nesse
      // caso segue no passo `filled`, que é o estado real do atendimento.
      if (session.status === 'form_completed') return session.capture.channelId ? 'confirmed' : 'filled';
      if (session.status === 'done') return 'done';
      return 'loading';
    }
    if (options.isError) return 'error';
    if (!options.data || createSession.isPending) return 'loading';
    if (options.data.funnels.length === 0) return 'empty';
    if (needsPrepare || forcePrepare) return 'prepare';
    // D15 (auto-gerar sem tela de preparar): se `createSession` falhou aqui, não há
    // formulário pra "segurar" o erro — vai para `'error'` com retry (ver Fix round 1).
    if (createSession.isError) return 'error';
    return 'loading';
  })();
  /** Só é `true` quando o `'error'` veio do `createSession` (fluxo automático D15) — usado
   * pra decidir a mensagem/ação do retry. `options.isError` já teria retornado antes. */
  const isAutoCreateError = view === 'error' && !sessionUuid && createSession.isError && !options.isError;

  const handleCancel = (): void => {
    if (!sessionUuid) { onClose(); return; }
    cancelSession.mutate(sessionUuid, { onSuccess: () => onClose(), onError: () => toast.error('Não foi possível cancelar o atendimento') });
  };
  const handleChangeContext = (): void => {
    if (!sessionUuid) return;
    cancelSession.mutate(sessionUuid, { onSuccess: () => { setSessionUuid(null); setForcePrepare(true); autoCreateTried.current = true; } });
  };
  const handleSkipForm = (): void => { if (sessionUuid) skipForm.mutate(sessionUuid, { onError: () => toast.error('Não foi possível concluir agora') }); };
  const handleComplete = (): void => { if (sessionUuid) complete.mutate(sessionUuid, { onError: () => toast.error('Não foi possível concluir agora') }); };
  // "Continuar atendimento": com formulário no funil (e ainda não respondido) abre a
  // etapa Formulário (Parte 3); sem formulário — ou formulário já respondido (retomada
  // de sessão `form_completed` após um `complete` que falhou) — conclui direto.
  // `wasExistingLead` (D5) NUNCA abre o formulário: o lead é de outro atendimento e
  // o backend recusa `POST …/form` com 409 (aplicação/Activity/pontos no lead alheio).
  const handleContinue = (): void => {
    if (session?.capture.formId && !session.formCompletedAt && !session.wasExistingLead) { setFormOpen(true); return; }
    handleComplete();
  };
  const handleNewSession = (): void => { setSessionUuid(null); setForcePrepare(needsPrepare); autoCreateTried.current = false; };

  // Etapa Formulário (mockup 04 A–D, `.drw-sub`) mostra "{cliente} · {funil}" — nas
  // demais views o header segue "{funil} · {unidade/equipe}" (Parte 2).
  const subtitle = session
    ? (view === 'form'
      ? [session.customer?.name, session.funnelName].filter(Boolean).join(' · ')
      : [session.funnelName, session.unitName ?? session.teamName].filter(Boolean).join(' · '))
    : undefined;
  const headerTone = view === 'done' ? 'green' : 'purple';
  const headerTitle = view === 'done'
    ? (session?.formCompletedAt || session?.confirmedAt ? 'Atendimento registrado' : 'Lead registrado')
    : view === 'form' ? CAPTURE_FORM_STEP_TITLE : 'Captura de leads';
  const busy = skipForm.isPending || complete.isPending || cancelSession.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="xl" className="!max-h-[92vh] flex flex-col">
      <div data-testid="lead-capture-modal" data-view={view} className="flex flex-col min-h-0">
        <ModalHeader>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <CaptureModalHeader
              icon={view === 'done' ? 'check' : view === 'form' ? 'list' : 'qr-code'}
              tone={headerTone}
              title={headerTitle}
              subtitle={view === 'prepare' ? 'Antes de gerar o QR Code' : subtitle}
            />
            <span className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg transition-colors flex-shrink-0"
              style={{ color: '#64748b' }}
              data-testid="capture-close"
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#1e293b'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
            >
              <Icon name="x" size={16} />
            </button>
          </div>
        </ModalHeader>

        {view === 'loading' && (
          <ModalContent>
            <div className="flex items-center justify-center gap-2 text-sm py-10" style={{ color: '#64748b' }} data-testid="capture-loading">
              <span className="w-4 h-4 rounded-full border-2 animate-spin motion-reduce:animate-none" style={{ borderColor: '#e9d5ff', borderTopColor: '#7c3aed' }} />
              Preparando o atendimento…
            </div>
          </ModalContent>
        )}

        {view === 'error' && (
          <>
            <ModalContent>
              <div className="text-center py-8 text-sm" style={{ color: '#b45309' }} data-testid="capture-error-message">
                {isAutoCreateError
                  ? (createErrorMessage ?? 'Não foi possível gerar o QR agora.')
                  : 'Não foi possível carregar o atendimento. Verifique sua conexão e tente de novo.'}
              </div>
            </ModalContent>
            <ModalFooter>
              <span className="flex-1" />
              <Button variant="ghost" onClick={onClose} data-testid="capture-error-close">Fechar</Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (isAutoCreateError) {
                    createSession.reset();
                    setCreateErrorMessage(null);
                    generate(prepare);
                  } else {
                    void options.refetch();
                    void sessionQuery.refetch();
                  }
                }}
                data-testid="capture-error-retry"
              >
                Tentar novamente
              </Button>
            </ModalFooter>
          </>
        )}

        {view === 'empty' && <CaptureEmptyState onClose={onClose} onConfigureFunnel={onConfigureFunnel} />}

        {view === 'prepare' && options.data && (
          <CapturePrepareStep options={options.data} value={prepare} onChange={setPrepare} onCancel={onClose} onGenerate={() => generate(prepare)} generating={createSession.isPending} />
        )}

        {view === 'qr' && session && (
          <CaptureQrStep session={session} qrDataUrl={sessionQuery.data?.qrDataUrl ?? ''} url={sessionQuery.data?.url ?? ''} onCancel={handleCancel} onChangeContext={handleChangeContext} cancelling={cancelSession.isPending} />
        )}

        {view === 'filled' && session && (
          <CaptureFilledStep session={session} onCancel={handleCancel} onSkip={handleSkipForm} onContinue={handleContinue} busy={busy} />
        )}

        {view === 'confirmed' && session && (
          <CaptureConfirmedStep session={session} onCancel={handleCancel} onSkip={handleSkipForm} onContinue={handleContinue} busy={busy} />
        )}

        {view === 'form' && session && sessionUuid && (
          <CaptureFormStep
            session={session}
            onSubmit={async (answers) => {
              // 1) respostas → sessão `form_completed` (o socket/polling também traz);
              // 2) conclui na hora — mockup 05-A: "Jornada concluída! +4 pontos".
              await submitForm.mutateAsync({ uuid: sessionUuid, answers });
              setFormOpen(false);
              complete.mutate(sessionUuid, { onError: () => toast.error('Não foi possível concluir agora') });
            }}
            onSkip={() => { setFormOpen(false); handleSkipForm(); }}
          />
        )}

        {view === 'done' && session && (
          <CaptureDoneStep session={session} points={sessionPoints(session)} gamificationPeriod={gamificationPeriod} onClose={onClose} onNewSession={handleNewSession} />
        )}
      </div>
    </Modal>
  );
};
